import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    useMapEvent,
} from "react-leaflet";
import L from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import icon from "assets/img/green-marker-icon.png";
import redIcon from "assets/img/red-marker-icon.png";
import blueIcon from "assets/img/blue-marker-icon.png";
import ripple from "assets/img/ripple.gif";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { updateOneRegistry } from "service/gatewayService";
import { ForbiddenErrorMessage, HttpStatus } from "constant";
import useGeolocation from "hooks/geoLocation";
import { OverlayAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { canAccess } from "utils/authorize-action";
import "assets/css/location.css";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import { isHttpSuccess } from "utils/functions";
import DefaultModal from "components/modals/ModalTemplate";
import { PowerCycleCheckBox } from "./Gateway";
import {
    addOrRemoveConfirmLocationPopup,
    isShowLocationPopup,
    fetchGWStatus,
} from "utils/gatewayFunctions";

const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    shadowAnchor: [13, 20],
    iconSize: [25, 41],
    popupAnchor: [0, -10],
});

const BlueIcon = L.icon({
    iconUrl: blueIcon,
    shadowUrl: iconShadow,
    shadowAnchor: [13, 20],
    iconSize: [25, 41],
    popupAnchor: [0, -10],
});

const RedIcon = L.icon({
    iconUrl: redIcon,
    shadowUrl: iconShadow,
    shadowAnchor: [13, 20],
    iconSize: [25, 41],
    popupAnchor: [0, -10],
});

const ConfirmModal = ({
    onConfirm = () => {},
    onCancel = () => {},
    modal,
}: {
    onConfirm: () => void;
    onCancel: () => void;
    modal: { show: boolean; type: string; content: string } | null;
}) => {
    const MODAL_CONFIRM = "confirm";
    const MODAL_SUCCESS = "success";
    const MODAL_ERROR = "err";

    const renderModalButton = () => {
        if (modal?.type === MODAL_ERROR || modal?.type === MODAL_SUCCESS) {
            return (
                <Button variant="primary" onClick={() => onCancel()}>
                    OK
                </Button>
            );
        }

        return (
            <>
                <Button
                    variant="secondary"
                    onClick={() => {
                        onCancel();
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="primary"
                    onClick={() => {
                        onConfirm();
                    }}
                >
                    OK
                </Button>
            </>
        );
    };

    const renderModalIcon = () => {
        if (modal?.type === MODAL_ERROR || modal?.type === MODAL_CONFIRM) {
            return <span className="material-icons">warning</span>;
        }
        return <span className="material-icons">done</span>;
    };

    const renderModalTitle = () => {
        if (modal?.type === MODAL_ERROR) {
            return <h3 className="mb-3">Error</h3>;
        } else if (modal?.type === MODAL_CONFIRM) {
            return <h3 className="mb-3">Confirmation</h3>;
        }
        return <h3 className="mb-3">Success</h3>;
    };

    // Just in case modal.show = false and other properties are changed also, then the buttons will be briefly changed based on these new properties
    if (!modal?.show) return <></>;

    return (
        <Modal
            centered
            show={!!modal?.show}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-sm"
            className={`no-header ${
                modal?.type === MODAL_SUCCESS ? "primary" : "danger"
            }`}
        >
            <Modal.Body className="text-center mt-3 mb-3">
                <div className="modal-icon-box">{renderModalIcon()}</div>
                {renderModalTitle()}
                <p className="mb-4">{modal?.content}</p>
                {renderModalButton()}
            </Modal.Body>
        </Modal>
    );
};

const LocationTab = (props: {
    registryDetails: any;
    loadMap: string;
    isActive: boolean;
}) => {
    const { registryDetails, loadMap, isActive } = props;
    const [initialCoordinates, setInitialCoordinates] = useState({
        lat: 0,
        lng: 0,
    });
    const [coordinates, setCoordinates] = useState({
        lat: 0,
        lng: 0,
    });
    const [newCoordinates, setNewCoordinates] = useState({
        lat: 0,
        lng: 0,
    });
    const [confirmedCoordinates, setConfirmedCoordinates] = useState({
        lat: 0,
        lng: 0,
    });
    const [markerInfo, setMarkerInfo] = useState<any>({
        gatewayName: "",
        gatewayId: "",
        status: false,
    });
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [modal, setModal] = useState<any>(null);
    const [tooltipTarget, setTooltipTarget] = useState<any>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [askLocationPopup, setAskLocationPopup] = useState(false);
    const [shownPopupBefore, setShownPopupBefore] = useState(false);
    const [mapZoom, setMapZoom] = useState(17);
    const [confirmationResult, setConfirmationResult] =
        useState<boolean>(false);
    const [mapInstance, setMapInstance] = useState<any>(null);

    const MODAL_CONFIRM = "confirm";
    const MODAL_SUCCESS = "success";
    const MODAL_ERROR = "err";
    const browserLocation = useGeolocation({}, isActive && confirmationResult);
    const gatewayId = registryDetails?.gateway_id;

    L.Marker.prototype.options.icon =
        markerInfo.status === true ? DefaultIcon : RedIcon;

    useEffect(() => {
        const fetch = async () => {
            if (registryDetails.location) {
                const latlng = {
                    lat: registryDetails.location[0],
                    lng: registryDetails.location[1],
                };

                setCoordinates(latlng);
                setInitialCoordinates(latlng);
                mapInstance?.setView(latlng);
            }

            setMarkerInfo({
                gatewayName: registryDetails.name,
                gatewayId: registryDetails.gateway_id,
                status: await fetchGWStatus(registryDetails.gateway_id),
            });
        };

        fetch();
    }, [loadMap, registryDetails, mapInstance]);

    // Reset the session to only trigger the popup once
    useEffect(() => {
        if (!isActive) {
            setShownPopupBefore(false);
            setConfirmationResult(false); // Reset confirmation when tab becomes inactive
        }
    }, [isActive]);

    useEffect(() => {
        if (
            !coordinates.lat &&
            loadMap === "loaded" &&
            isActive &&
            !isShowLocationPopup(gatewayId) &&
            !shownPopupBefore
        ) {
            setShowConfirmModal(true);
            setShownPopupBefore(true);
        }
    }, [loadMap, isActive, coordinates.lat, gatewayId, shownPopupBefore]);

    // Handle geolocation response when user confirms YES
    useEffect(() => {
        if (confirmationResult) {
            if (
                browserLocation.latitude &&
                browserLocation.longitude &&
                !browserLocation.loading &&
                !browserLocation.error
            ) {
                const latlng = {
                    lat: browserLocation.latitude,
                    lng: browserLocation.longitude,
                };

                // Use the browser location once it's available
                setCoordinates(latlng);
                setInitialCoordinates(latlng);

                // Call updateLocation asynchronously
                const applyLocation = async () => {
                    await updateLocation(new L.LatLng(latlng.lat, latlng.lng));

                    mapInstance?.setView(latlng);
                };
                applyLocation();

                // Reset confirmation result to prevent repeated executions
                setConfirmationResult(false);
            } else if (!browserLocation.loading && browserLocation.error) {
                const errorMessage =
                    "Location access was denied. Please enable location permissions in your browser settings or manually set the gateway location.";

                setModal({
                    show: true,
                    type: MODAL_ERROR,
                    content: errorMessage,
                });

                // Reset confirmation result
                setConfirmationResult(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmationResult, browserLocation]);

    const updateLocation = async (location?: L.LatLng) => {
        try {
            const lat = location?.lat ?? confirmedCoordinates.lat;
            const lng = location?.lng ?? confirmedCoordinates.lng;
            const body = {
                location: [lat, lng],
            };
            const response: any = await updateOneRegistry(
                registryDetails.gateway_id,
                body
            );
            if (isHttpSuccess(response.status)) {
                setIsLoading(false);
                setEditMode(false);

                setCoordinates({
                    lat: lat,
                    lng: lng,
                });

                setInitialCoordinates({
                    lat: lat,
                    lng: lng,
                });
                if (!location) {
                    setModal({
                        show: true,
                        type: MODAL_SUCCESS,
                        content:
                            response.data?.message ||
                            "Gateway location updated.",
                    });
                }
            } else if (response.status === HttpStatus.FORBIDDEN) {
                setIsLoading(false);
                setModal({
                    show: true,
                    type: MODAL_ERROR,
                    content: ForbiddenErrorMessage,
                });
            } else {
                setIsLoading(false);
                setModal({
                    show: true,
                    type: MODAL_ERROR,
                    content:
                        response.data?.message ||
                        "Something went wrong. Please try again.",
                });
            }
        } catch (err: any) {
            setIsLoading(false);
            setModal({
                show: true,
                type: MODAL_ERROR,
                content:
                    err.response?.data?.message ||
                    "Something went wrong. Please try again.",
            });
        }
    };

    const Layer = ({
        coordinates,
    }: {
        coordinates: { lat: number; lng: number };
    }) => {
        const map = useMap();
        let isDragging = false;
        let mousedownTimer: any;
        const provider: any = new OpenStreetMapProvider();

        // @ts-ignore
        const searchControl: any = new GeoSearchControl({
            provider: provider,
            style: "bar",
            marker: {
                icon: BlueIcon,
                draggable: false,
            },
            maxMarkers: 1,
            showPopup: true,
            autoClose: true,
            keepResult: true,
        });

        useEffect(() => {
            map.addControl(searchControl);
            // Only invalidate size when the map is first loaded or when container size might change
            map.invalidateSize();

            return () => {
                map.removeControl(searchControl);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Track zoom changes to preserve zoom level
        useMapEvent("zoomend", () => {
            setMapZoom(map.getZoom());
        });

        const handleLongPress = (e: any) => {
            if (isDragging) return;
            setNewCoordinates({
                lat: e.latlng.lat,
                lng: e.latlng.lng,
            });

            if (!canAccess("gateway:update")) {
                setTooltipTarget(e.target?._container);
                setShowTooltip(true);
                setTimeout(() => {
                    setShowTooltip(false);
                }, 2000);
            } else {
                setModal({
                    show: true,
                    type: MODAL_CONFIRM,
                    content: `Do you want to change the gateway location to the new coordinates? You still need to click on "Save" to finalise this change.`,
                });
            }
        };

        useMapEvent("mousedown", (e) => {
            mousedownTimer = setTimeout(() => {
                handleLongPress(e);
            }, 800);

            isDragging = false;
        });

        useMapEvent("contextmenu", (e) => {
            mousedownTimer = setTimeout(() => {
                handleLongPress(e);
            }, 200);

            isDragging = false;
        });

        useMapEvent("mousemove", (e) => {
            isDragging = true;
        });

        useMapEvent("mouseup", (e) => {
            if (mousedownTimer) {
                clearTimeout(mousedownTimer);
            }
            isDragging = false;
        });

        const addNewMarker = () => {
            return (
                <Marker
                    position={[
                        confirmedCoordinates.lat,
                        confirmedCoordinates.lng,
                    ]}
                >
                    <Popup
                        position={[
                            confirmedCoordinates.lat,
                            confirmedCoordinates.lng,
                        ]}
                    >
                        {" "}
                        <div className="w-100 h-100 text-center">
                            {markerInfo.gatewayName}
                        </div>
                        <div>{markerInfo.gatewayId}</div>
                    </Popup>
                </Marker>
            );
        };

        return (
            <>
                <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <Marker position={[coordinates.lat, coordinates.lng]}>
                    <Popup position={[coordinates.lat, coordinates.lng]}>
                        {" "}
                        <div className="w-100 h-100 text-center">
                            {markerInfo.gatewayName}
                        </div>
                        <div>{markerInfo.gatewayId}</div>
                    </Popup>
                </Marker>
                {confirmedCoordinates && editMode ? addNewMarker() : ""}
            </>
        );
    };

    const closeConfirmModal = () => {
        setShowConfirmModal(false);
    };

    const confirmUseCurrentLocation = (confirmation: "YES" | "NO" = "NO") => {
        closeConfirmModal();

        // Set the confirmation result state to trigger geolocation
        setConfirmationResult(confirmation === "YES");

        if (askLocationPopup) {
            addOrRemoveConfirmLocationPopup(registryDetails.gateway_id);
        }
    };

    return (
        <div className="location-box">
            <div className="pt-5 pb-5">
                <MapContainer
                    center={[coordinates.lat, coordinates.lng]}
                    zoom={mapZoom}
                    scrollWheelZoom={true}
                    className="map-container"
                    whenCreated={(mapInstance) => {
                        setMapInstance(mapInstance);
                    }}
                >
                    <Layer coordinates={coordinates} />
                </MapContainer>
            </div>
            <div className="text-center">
                <h6>
                    Long tap on any location to change the marker to that
                    location.
                </h6>
                {editMode ? (
                    <>
                        <Button
                            variant="danger"
                            className="mt-2 mb-1 ml-1 mr-1 edit-mode-button"
                            onClick={() => {
                                setConfirmedCoordinates({
                                    lat: 0,
                                    lng: 0,
                                });
                                setCoordinates({
                                    lat: initialCoordinates.lat,
                                    lng: initialCoordinates.lng,
                                });
                                setEditMode(false);
                            }}
                        >
                            BACK
                        </Button>
                        <Button
                            className="mt-2 mb-1 ml-1 mr-1 edit-mode-button"
                            variant="primary"
                            onClick={() => {
                                setIsLoading(true);
                                updateLocation();
                            }}
                        >
                            SAVE
                        </Button>
                    </>
                ) : (
                    ""
                )}
            </div>

            <ConfirmModal
                onConfirm={() => {
                    setModal(null);
                    setCoordinates({
                        lat: newCoordinates.lat,
                        lng: newCoordinates.lng,
                    });
                    setEditMode(true);
                    setConfirmedCoordinates({
                        lat: newCoordinates.lat,
                        lng: newCoordinates.lng,
                    });
                }}
                onCancel={() => setModal(null)}
                modal={modal}
            />

            <DefaultModal
                modalShow={showConfirmModal}
                setModalShow={setShowConfirmModal}
                modalType="custom"
                modalContent={
                    <>
                        <div>
                            Do you want to set the Gateway’s location by using
                            your device’s current location?
                        </div>
                        <PowerCycleCheckBox
                            custom
                            id="confirm-location"
                            className="mb-3 mt-3"
                            checked={askLocationPopup}
                            onChange={(e) => {
                                setAskLocationPopup(e.target.checked);
                            }}
                            label={
                                <span className="ml-1">Don’t ask again</span>
                            }
                        />
                    </>
                }
                modalTitle={
                    <>
                        No location found. <br />
                        Set location now.
                    </>
                }
                modalButton={() => (
                    <>
                        <Button
                            variant="danger"
                            onClick={() => {
                                confirmUseCurrentLocation("NO");
                            }}
                        >
                            NO
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => confirmUseCurrentLocation("YES")}
                        >
                            YES
                        </Button>
                    </>
                )}
            />
            <img
                className={
                    isLoading ? "loader-gif d-block" : "loader-gif d-none"
                }
                src={ripple}
                alt="loading"
            />
            <OverlayAuthorizeTooltip
                target={tooltipTarget}
                show={showTooltip}
            />
        </div>
    );
};

export default LocationTab;
